import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SpeedDial from '@mui/material/SpeedDial';
import { useTheme } from '@mui/material/styles'; // Importa useTheme
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import { useSelector } from 'react-redux'; // Importa useSelector per determinare se l'utente è admin

const HelpGuideDrawer = () => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState(false);

  // Determina se l'utente è admin recuperando lo stato da Redux
  const { user } = useSelector((state) => state.auth);
  const isCurrentUserAdmin = user && user.roles && user.roles.includes('admin');

  const theme = useTheme(); // Inizializza useTheme

  const toggleDrawer = (newOpen) => () => {
    setOpenDrawer(newOpen);
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  // Funzione per renderizzare il contenuto di una sezione dell'accordion
  const renderSectionContent = (sectionTitle) => {
    switch (sectionTitle) {
      case 'introduzione':
        return (
          <Typography variant="body1" paragraph>
            Benvenuto nella <strong>Guida per l'Inserimento e la Gestione delle Attività</strong>!
            Sono qui per aiutarti a capire come usare questa schermata.
          </Typography>
        );
      case 'inserimento':
        return (
          <>
            <Typography variant="body1" paragraph>
              Per inserire una nuova attività, segui questi passi:
            </Typography>
            <ul>
              <li><strong>Data:</strong> Seleziona la data dell'attività (di default è la <strong>data odierna</strong>).</li>
              <li><strong>Orario inizio / Orario fine:</strong> Inserisci l'orario di inizio e di fine dell'attività. Assicurati che l'orario di fine sia <strong>successivo a quello di inizio</strong>.</li>
              <li><strong>Tipologia:</strong> Scegli la <strong>tipologia di attività</strong> dal menu a tendina (es. Chiamata, Incontro, Email, ecc.).</li>
              <li><strong>Contatti:</strong> Cerca e seleziona <strong>uno o più contatti</strong> (Stakeholder) con cui è associata l'attività. Puoi digitare il loro nome, cognome o codice per trovarli. Puoi selezionare più contatti per la stessa azione.</li>
              <li><strong>Note:</strong> Aggiungi qualsiasi <strong>nota o dettaglio</strong> rilevante sull'attività.</li>
              <li><strong>Salva:</strong> Clicca sul pulsante <strong>"Salva"</strong> (in verde) in fondo al modulo.</li>
            </ul>
          </>
        );
      case 'tabella':
        return (
          <>
            <Typography variant="body1" paragraph>
              Le attività che inserisci per la data selezionata (di default, quelle di oggi) vengono visualizzate nella <strong>"Tabella Riassuntiva"</strong> sotto il modulo di inserimento.
            </Typography>
            <ul>
              <li>La tabella mostra le informazioni chiave come <strong>codice contatto, tipologia, orario di inizio e fine</strong>.</li>
              <li><strong>Funzionalità Aggiuntiva (Solo Admin):</strong> Se sei loggato come <strong>Amministratore</strong>, vedrai anche i campi <strong>"Nome"</strong> e <strong>"Cognome"</strong> dei contatti, oltre al loro codice. Questo ti fornisce una <strong>visione più completa</strong> dei dati.</li>
              <li>Se non vedi attività, potrebbe non essercene nessuna inserita per questa data, oppure ci sono stati problemi di caricamento (verrai avvisato da un messaggio).</li>
            </ul>
          </>
        );
      case 'funzioni_tabella_datagrid':
          return (
            <>
              <Typography variant="body1" paragraph>
                La <strong>tabella riassuntiva</strong> delle attività di oggi offre diverse <strong>funzionalità interattive</strong> per gestire i dati.
              </Typography>
              <ul>
                <li><strong>Impaginazione:</strong> In fondo alla tabella, puoi navigare tra le pagine e scegliere <strong>quante righe visualizzare per pagina</strong> (es. 5, 10, 20).</li>
                <li><strong>Ordinamento delle Colonne:</strong> Clicca sull'<strong>intestazione di una colonna</strong> (es. "Tipologia", "Ora Inizio") per ordinare i dati in ordine <strong>crescente o decrescente</strong> in base a quella colonna.</li>
                <li><strong>NoRowsOverlay:</strong> Se non ci sono attività per la data selezionata, la tabella mostrerà un messaggio <strong>"Nessuna attività inserita per questa data"</strong>.</li>
                <li><strong>Dettaglio Azione (Doppio Clic):</strong> Come descritto nella sezione "Modifica", un <strong>doppio clic</strong> su una riga apre il dialogo per modificare (o cancellare, se Admin) quell'attività specifica.</li>
              </ul>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Dettaglio Colonne:
              </Typography>
              <ul>
                <li><strong>ID:</strong> <strong>Identificativo univoco</strong> dell'attività (solitamente nascosto o poco rilevante per l'utente finale).</li>
                <li><strong>Nome / Cognome:</strong> (Visibili solo agli Amministratori) Nome e cognome dello stakeholder associato.</li>
                <li><strong>Codice Contatto:</strong> Il <strong>codice identificativo</strong> dello stakeholder.</li>
                <li><strong>Tipologia Attività:</strong> La <strong>categoria dell'attività</strong> (es. Chiamata, Incontro).</li>
                <li><strong>Ora Inizio:</strong> L'<strong>orario di inizio</strong> dell'attività.</li>
                <li><strong>Ora Fine:</strong> L'<strong>orario di fine</strong> dell'attività.</li>
                <li><strong>Totale ore:</strong> La <strong>durata totale</strong> dell'attività in ore e minuti. Questa colonna può essere <strong>evidenziata in giallo</strong> se le ore totali sono `00:00` o uguali/superiori a `08:00`.</li>
                <li><strong>Note:</strong> Eventuali <strong>dettagli aggiuntivi</strong> sull'attività.</li>
              </ul>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Menu delle Colonne (Tre puntini):
              </Typography>
              <Typography variant="body1" paragraph>
                Cliccando sull'icona con i <strong>tre puntini verticali</strong> (menu kebab) nell'intestazione di ogni colonna, si apre un menu con <strong>opzioni specifiche</strong> per quella colonna:
              </Typography>
              <ul>
                <li><strong>Sort Asc/Desc:</strong> Ordina i dati della colonna in <strong>ordine crescente o decrescente</strong>.</li>
                <li><strong>Hide column:</strong> <strong>Nasconde</strong> la colonna dalla visualizzazione.</li>
                <li><strong>Filter:</strong> Abilita un <strong>mini-pannello per filtrare</strong> i dati della colonna (es. filtra per un valore specifico, per intervallo di date, ecc.).</li>
                <li><strong>Pin column:</strong> <strong>Blocca la colonna</strong> a sinistra o a destra, in modo che rimanga visibile anche scorrendo orizzontalmente.</li>
                <li><strong>Autosize column:</strong> Adatta <strong>automaticamente la larghezza</strong> della colonna al contenuto.</li>
                <li><strong>Columns:</strong> Apre il pannello di <strong>gestione delle colonne</strong> (simile al pulsante "Mostra Colonne" nella toolbar) per visualizzare o nascondere rapidamente più colonne.</li>
              </ul>
            </>
          );
      case 'modifica':
        return (
          <>
            <Typography variant="body1" paragraph>
              La modifica delle attività è una funzionalità disponibile <strong>esclusivamente per gli Amministratori (Admin)</strong>.
            </Typography>
            <ol>
              <li><strong>Doppio Clic sulla Riga:</strong> Nella "Tabella Riassuntiva", fai un <strong>doppio clic</strong> sulla riga dell'attività che desideri modificare.</li>
              <li><strong>Apertura del Dialogo di Modifica:</strong> Si aprirà un <strong>pop-up</strong> ("Modifica azione di...") con i dettagli dell'attività precompilati.</li>
              <li><strong>Modifica i Campi:</strong> Puoi modificare la <strong>data, gli orari, la tipologia e le note</strong>.
                <ul>
                  <li><strong>Importante:</strong> Non puoi modificare il contatto direttamente da questo dialogo, poiché un'azione è associata a un singolo contatto per la modifica.</li>
                </ul>
              </li>
              <li><strong>Salva Modifiche:</strong> Clicca sul pulsante <strong>"Salva Modifiche"</strong> (in verde) per salvare le modifiche.</li>
              <li><strong>Annulla:</strong> Clicca su <strong>"Annulla"</strong> per chiudere il dialogo senza salvare.</li>
            </ol>
          </>
        );
      case 'cancellazione':
        return (
          <>
            <Typography variant="body1" paragraph>
              La cancellazione delle attività è una funzionalità disponibile <strong>esclusivamente per gli Amministratori (Admin)</strong>.
            </Typography>
            <ol>
              <li><strong>Doppio Clic sulla Riga:</strong> Come per la modifica, fai un <strong>doppio clic</strong> sulla riga dell'attività che vuoi cancellare. Si aprirà il dialogo di modifica.</li>
              <li><strong>Pulsante "Cancella":</strong> Nel dialogo di modifica, clicca sul pulsante <strong>"Cancella"</strong> (in rosso).</li>
              <li><strong>Conferma Cancellazione:</strong> Si aprirà un <strong>nuovo dialogo di conferma</strong>. Ti verrà chiesto se sei sicuro di voler cancellare l'azione, poiché l'operazione è <strong>irreversibile</strong>.</li>
              <li><strong>Conferma o Annulla:</strong>
                <ul>
                  <li>Clicca <strong>"Sì, Cancella"</strong> (in rosso) per procedere con la cancellazione definitiva.</li>
                  <li>Clicca <strong>"No, Annulla"</strong> per tornare indietro senza cancellare.</li>
                </ul>
              </li>
            </ol>
          </>
        );
      case 'feedback_messaggi':
        return (
          <>
            <Typography variant="body1" paragraph>
              Vedrai un piccolo messaggio pop-up (chiamato "Snackbar") apparire in fondo allo schermo:
            </Typography>
            <ul>
              <li><strong>Verde (Successo):</strong> Indica che l'operazione (es. salvataggio, modifica, cancellazione) è andata a buon fine.</li>
              <li><strong>Arancione (Avviso):</strong> Indica un avviso (es. "Nessuna azione inserita" se non sono stati trovati contatti validi).</li>
              <li><strong>Rosso (Errore):</strong> Indica che c'è stato un <strong>errore</strong> durante l'operazione e l'azione <strong>non è stata completata</strong>. Il messaggio ti darà <strong>più dettagli sull'errore</strong>.</li>
            </ul>
          </>
        );
      case 'problemi_comuni':
        return (
          <>
            <ul>
              <li><strong>Domanda:</strong> Cosa significa "Campo richiesto!"?
                <br />
                <strong>Risposta:</strong> Significa che non hai compilato uno dei <strong>campi obbligatori</strong> (Data, Orario inizio, Orario fine, Tipologia, Contatti) prima di cliccare "Salva". Assicurati che tutti i campi obbligatori siano riempiti.</li>
              <li><strong>Domanda:</strong> Non vedo i campi "Nome" e "Cognome" nella tabella. Perché?
                <br />
                <strong>Risposta:</strong> I campi "Nome" e "Cognome" sono visibili nella tabella solo se sei loggato come <strong>Amministratore (Admin)</strong>. Altrimenti, vedrai solo il "Codice Contatto" per motivi di <strong>privacy o ruolo utente</strong>.</li>
              <li><strong>Domanda:</strong> Cosa succede se Orario Inizio è dopo Orario Fine?
                <br />
                <strong>Risposta:</strong> L'applicazione non ha una <strong>validazione specifica per impedirlo</strong> a livello di UI. È tua responsabilità assicurarti che gli orari siano logici. Tuttavia, il backend potrebbe avere delle regole che impediscono un salvataggio non valido.</li>
            </ul>
          </>
        );
      default:
        return null;
    }
  };

  const accordionSections = [
    { id: 'introduzione', title: 'Benvenuto nella Guida!', content: renderSectionContent('introduzione') },
    { id: 'inserimento', title: '1. Inserimento di una Nuova Attività', content: renderSectionContent('inserimento') },
    { id: 'tabella', title: '2. Tabella delle Attività del Giorno', content: renderSectionContent('tabella') },
    { id: 'funzioni_tabella_datagrid', title: '3. Funzionalità della Tabella Riassuntiva', content: renderSectionContent('funzioni_tabella_datagrid') },
    { id: 'modifica', title: '4. Modifica di un\'Attività Esistente (Solo Admin)', content: renderSectionContent('modifica'), adminOnly: true },
    { id: 'cancellazione', title: '5. Cancellazione di un\'Attività Esistente (Solo Admin)', content: renderSectionContent('cancellazione'), adminOnly: true },
    { id: 'feedback_messaggi', title: '6. Messaggi di Feedback (Snackbar)', content: renderSectionContent('feedback_messaggi') },
    { id: 'problemi_comuni', title: '7. Problemi Comuni e Risoluzione', content: renderSectionContent('problemi_comuni') },
  ];

  return (
    <>
      <SpeedDial
        ariaLabel="Hai bisogno di aiuto?"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        icon={<HelpOutlineIcon />}
        onClick={toggleDrawer(true)}
        FabProps={{
            sx: {
                bgcolor: 'primary.main',
                '&:hover': {
                    bgcolor: 'primary.dark',
                },
            }
        }}
        tooltipTitle="Hai bisogno di aiuto?"
        tooltipOpen
      >
      </SpeedDial>

      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 450, md: 550 },
            zIndex: theme.zIndex.modal + 1 || 2000, // Assicurati che lo z-index sia definito
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderBottom: '1px solid lightgrey',
            // Applica il padding-top qui, per spostare il contenuto sotto la navbar
            pt: { xs: `calc(${theme.spacing(2)} + 56px)`, sm: `calc(${theme.spacing(2)} + 64px)` },
          }}
        >
          <Typography variant="h6" component="div">
            Guida per "Inserisci Azione"
          </Typography>
          <IconButton onClick={toggleDrawer(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 2 }}>
          {accordionSections.map((section) => (
            (!section.adminOnly || isCurrentUserAdmin) && (
              <Accordion
                key={section.id}
                expanded={expandedAccordion === section.id}
                onChange={handleAccordionChange(section.id)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`${section.id}-content`}
                  id={`${section.id}-header`}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{section.title}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {section.content}
                </AccordionDetails>
              </Accordion>
            )
          ))}
        </Box>
      </Drawer>
    </>
  );
};

export default HelpGuideDrawer;